import { type Feature, PermissionLevel, type Role } from '@/enum/global.enum'
import type { IActiveOrg } from '@/interface/base.interface'

export interface PermissionCheckOptions {
  requireViewPermission?: boolean
  requireAddPermission?: boolean
  requireEditPermission?: boolean
  requireDeletePermission?: boolean
}

/**
 * Check if user has required role
 * @param userRole - The user's role
 * @param requiredRoles - Array of roles that are allowed
 * @returns boolean indicating if user has required role
 */
export const hasRequiredRole = (userRole: Role | string | undefined, requiredRoles: Role[] | undefined): boolean => {
  if (!requiredRoles || requiredRoles.length === 0) return true
  if (!userRole) return false

  // Convert userRole to string for comparison
  const userRoleString = typeof userRole === 'string' ? userRole : userRole
  return requiredRoles.some((role) => role === userRoleString)
}

/**
 * Check if user has required feature permission
 * @param userFeaturePermissions - User's feature permissions
 * @param requiredFeature - Required feature
 * @param options - Permission level requirements
 * @returns boolean indicating if user has required feature permission
 */
export const hasRequiredFeature = (
  userFeaturePermissions: { feature: Feature; permission: PermissionLevel }[] | undefined,
  requiredFeature: Feature | undefined,
  options: PermissionCheckOptions = {}
): boolean => {
  if (!requiredFeature) return true
  if (!userFeaturePermissions || userFeaturePermissions.length === 0) return false

  const userPermission = userFeaturePermissions.find((permission) => permission.feature === requiredFeature)
  if (!userPermission) return false

  // If no specific permission level is required, just having the feature is enough
  if (
    !options.requireViewPermission &&
    !options.requireAddPermission &&
    !options.requireEditPermission &&
    !options.requireDeletePermission
  ) {
    return true
  }

  // Check specific permission levels
  const userPermissionLevel = userPermission.permission

  if (options.requireDeletePermission && userPermissionLevel === PermissionLevel.DELETE) return true
  if (
    options.requireEditPermission &&
    (userPermissionLevel === PermissionLevel.EDIT || userPermissionLevel === PermissionLevel.DELETE)
  )
    return true
  if (
    options.requireAddPermission &&
    (userPermissionLevel === PermissionLevel.ADD ||
      userPermissionLevel === PermissionLevel.EDIT ||
      userPermissionLevel === PermissionLevel.DELETE)
  )
    return true
  if (
    options.requireViewPermission &&
    (userPermissionLevel === PermissionLevel.VIEW ||
      userPermissionLevel === PermissionLevel.ADD ||
      userPermissionLevel === PermissionLevel.EDIT ||
      userPermissionLevel === PermissionLevel.DELETE)
  )
    return true

  return false
}

/**
 * Comprehensive permission check for navigation items
 * @param activeOrg - Active organisation context
 * @param requiredRole - Required roles for the item
 * @param requiredFeature - Required feature for the item
 * @param permissionOptions - Specific permission level requirements
 * @returns boolean indicating if user has all required permissions
 */
export const hasNavigationPermission = (
  activeOrg: IActiveOrg | undefined,
  requiredRole: Role[] | undefined,
  requiredFeature: Feature | undefined,
  permissionOptions: PermissionCheckOptions = {}
): boolean => {
  if (!activeOrg) return false

  // Check role permission
  const isRoleAllowed = hasRequiredRole(activeOrg.role, requiredRole)
  if (!isRoleAllowed) return false

  // Check feature permission
  const isFeatureAllowed = hasRequiredFeature(activeOrg.featurePermissions, requiredFeature, permissionOptions)
  if (!isFeatureAllowed) return false

  return true
}

/**
 * Check if user has any of the required roles
 * @param userRole - The user's role
 * @param requiredRoles - Array of roles that are allowed
 * @returns boolean indicating if user has any of the required roles
 */
export const hasAnyRequiredRole = (userRole: Role | string | undefined, requiredRoles: Role[] | undefined): boolean => {
  return hasRequiredRole(userRole, requiredRoles)
}

/**
 * Check if user has all required roles (for cases where multiple roles are required)
 * @param userRole - The user's role
 * @param requiredRoles - Array of roles that are all required
 * @returns boolean indicating if user has all required roles
 */
export const hasAllRequiredRoles = (
  userRole: Role | string | undefined,
  requiredRoles: Role[] | undefined
): boolean => {
  if (!requiredRoles || requiredRoles.length === 0) return true
  if (!userRole) return false

  const userRoleString = typeof userRole === 'string' ? userRole : userRole
  return requiredRoles.every((role) => role === userRoleString)
}

/**
 * Get user's permission level for a specific feature
 * @param userFeaturePermissions - User's feature permissions
 * @param feature - The feature to check
 * @returns The permission level or undefined if not found
 */
export const getUserFeaturePermissionLevel = (
  userFeaturePermissions: { feature: Feature; permission: PermissionLevel }[] | undefined,
  feature: Feature
): PermissionLevel | undefined => {
  if (!userFeaturePermissions || userFeaturePermissions.length === 0) return undefined

  const permission = userFeaturePermissions.find((p) => p.feature === feature)
  return permission?.permission
}
